// All iOS TypeScript Functions here...

import { z } from "zod";
import {
  disableHapticStore,
  disableSoundStore,
} from "../domains/user/stores/nativeSettingsStore";
import { sessionStore } from "../lib/store";
import { SoundEffect } from "../interfaces";
import { getCurrentOS } from "./utils";
import type { FidDeleteResult } from "../services/firebase";

const DEFAULT_NATIVE_TIMEOUT_MS = 15_000;
const OAUTH_TIMEOUT_MS = 300_000; // 5 min for user-interactive flows

// Zod schemas for iOS native bridge responses
const IOSNotificationStatusSchema = z.enum([
  "notDetermined",
  "authorized",
  "denied",
]);
const IOSSafeAreaSchema = z.tuple([z.number(), z.number()]);

const IOSSignInErrorSchema = z.object({
  authProvider: z.enum(["apple", "google"]),
  success: z.literal(false),
  error: z.string(),
});

const IOSAppleSignInSuccessSchema = z.object({
  authProvider: z.literal("apple"),
  success: z.literal(true),
  idToken: z.string(),
  rawNonce: z.string(),
  user: z.string(),
  email: z.string(),
  firstname: z.string(),
  lastname: z.string(),
  state: z.string(),
  authorizationCode: z.string(),
});

const IOSGoogleSignInSuccessSchema = z.object({
  authProvider: z.literal("google"),
  success: z.literal(true),
  idToken: z.string(),
  refreshToken: z.string(),
  accessToken: z.string(),
  rawNonce: z.string(),
});

/**
 * The `services` message handler, or null outside the iOS shell.
 * Exported so `native-auth-session.ts` can post on the same channel
 * instead of re-reading `window.webkit` and owning a second truth.
 */
export const getServicesHandler = () =>
  window.webkit?.messageHandlers.services ?? null;

function compactLog(value: string): string {
  return value.replace(/\s+/g, " ").trim().slice(0, 1200);
}

function safeStringify(value: unknown): string {
  try {
    const seen = new WeakSet<object>();
    const json = JSON.stringify(value, (_key, current) => {
      if (current instanceof Error) {
        return {
          name: current.name,
          message: current.message,
          stack: current.stack,
        };
      }
      if (typeof current === "object" && current !== null) {
        if (seen.has(current)) return "[Circular]";
        seen.add(current);
      }
      return current;
    });
    return compactLog(json ?? String(value));
  } catch {
    return compactLog(String(value));
  }
}

function errorToLogMessage(error: unknown): string {
  if (error instanceof Error) {
    return compactLog(`${error.name}: ${error.message}`);
  }
  return safeStringify(error);
}

function summarizeZodIssues(issues: z.ZodIssue[]): string {
  if (issues.length === 0) return "none";
  return compactLog(
    issues
      .map((issue) => {
        const path = issue.path.length > 0 ? issue.path.join(".") : "<root>";
        return `${path}:${issue.code}:${issue.message}`;
      })
      .join(" | ")
  );
}

/**
 * Checks if running in the iOS app (WKWebView with bridge available).
 * Evaluates at call time to avoid race conditions where the iOS bridge
 * isn't available yet when JS first runs.
 * Result is memoized once the bridge is confirmed ready.
 */
let _isIOSAppCache: boolean | null = null;

export function isIOSApp(): boolean {
  if (_isIOSAppCache === true) return true;
  const ready =
    window.webkit !== undefined &&
    window.webkit.messageHandlers.services !== undefined;
  if (ready) _isIOSAppCache = true;
  return ready;
}

/**
 * Helper function to create a Promise-based callback for iOS native methods.
 * Generates a unique callback name and sets up the Promise resolution.
 *
 * @param method - The iOS method name to call
 * @param schema - Zod schema to validate and type the response
 * @param additionalParams - Additional parameters to include in the request
 * @param timeoutMs - Timeout in milliseconds (default 15s, use OAUTH_TIMEOUT_MS for sign-in)
 * @returns A Promise that resolves when the native callback is invoked
 */
function createIOSCallbackPromise<T extends z.ZodTypeAny>(
  method: string,
  schema: T,
  additionalParams: Record<string, unknown> = {},
  timeoutMs = DEFAULT_NATIVE_TIMEOUT_MS,
  fallbackOnTimeout?: z.infer<T>
): Promise<z.infer<T>> {
  const handler = getServicesHandler();
  if (handler === null) {
    return Promise.reject(new Error("Not running on native iOS"));
  }

  return new Promise<z.infer<T>>((resolve, reject) => {
    const callbackName =
      `${method}Callback_${Math.random().toString(36).slice(2, 9)}` as const;

    const timeout = window.setTimeout(() => {
      delete window[callbackName];
      if (fallbackOnTimeout === undefined) {
        reject(new Error(`iOS callback timeout: ${method}`));
      } else {
        resolve(fallbackOnTimeout);
      }
    }, timeoutMs);

    window[callbackName] = (...args: unknown[]): void => {
      window.clearTimeout(timeout);
      delete window[callbackName];
      const data = args.length === 1 ? args[0] : args;
      const parsed = schema.safeParse(data);
      if (parsed.success) {
        resolve(parsed.data);
      } else {
        const issuesSummary = summarizeZodIssues(parsed.error.issues);
        console.error(
          `iOS callback validation failed method=${method} payload=${safeStringify(
            data
          )} issues=${issuesSummary}`
        );
        reject(new Error(`iOS callback validation failed: ${method}`));
      }
    };

    try {
      handler.postMessage({
        method,
        callback: callbackName,
        ...additionalParams,
      });
    } catch (error) {
      window.clearTimeout(timeout);
      delete window[callbackName];
      console.error(
        `iOS callback invocation failed method=${method} error="${errorToLogMessage(
          error
        )}"`
      );
      reject(error instanceof Error ? error : new Error(String(error)));
    }
  });
}

export const iosSetBackgroundColor = (color: string): void => {
  const request = {
    method: "setBackgroundColor",
    color: color,
  };
  getServicesHandler()?.postMessage(request);
};

export const iosRequestPushAuthorization = async (): Promise<boolean> => {
  return createIOSCallbackPromise(
    "requestPushAuthorization",
    z.boolean(),
    {},
    DEFAULT_NATIVE_TIMEOUT_MS,
    false
  );
};

export const iosGetFirebaseToken = async (): Promise<string | null> => {
  const token = await createIOSCallbackPromise("getFirebaseToken", z.string());
  return token || null;
};

// 5s + fallback `false`: old native builds without this handler fall through
// silently — the callback never fires, the timeout resolves to false. Long
// enough that a new client on a slow network legitimately completes the
// Firebase Installation delete; short enough to not stall an old client for
// 15s on every session-start sync.
const DELETE_FIREBASE_INSTALLATION_TIMEOUT_MS = 5_000;

// Deletes the Firebase Installation ID entirely (FID + every associated
// token + topic subscriptions). The Firebase iOS SDK persists the FID in
// the Keychain, which survives app uninstall — so without an explicit
// `Installations.delete`, a reinstall can carry over a stale FID whose
// server-side APNs binding is corrupted, silently breaking push delivery
// even after Apple mints a new APNs token. The native handler is expected
// to re-subscribe the device to startup topics after the delete completes.
//
// iOS has no JS-visible feature detection (the bridge surface isn't
// enumerable from JS), so a timeout is the only "old client" signal — old
// builds silently drop the unknown message and the callback never fires.
// We pass `null` as the timeout fallback so we can map it distinctly to
// `bridge_missing`; a Promise rejection (postMessage throw, validation
// fail) maps to `bridge_invocation_failed`.
export const iosDeleteFirebaseInstallation =
  async (): Promise<FidDeleteResult> => {
    try {
      const result = await createIOSCallbackPromise(
        "deleteFirebaseInstallation",
        z.boolean().nullable(),
        {},
        DELETE_FIREBASE_INSTALLATION_TIMEOUT_MS,
        null
      );
      if (result === null) return { status: "bridge_missing" };
      return result
        ? { status: "deleted" }
        : { status: "native_delete_failed" };
    } catch (error) {
      // console.warn, not console.error: captureConsoleIntegration only captures
      // "error" level. The failure is already reported structurally at info level
      // by ensureFreshNativeFcm (reason: bridge_invocation_failed) — a console.error
      // here would double-report it, escalated to an error-severity Sentry issue.
      console.warn(
        `iosDeleteFirebaseInstallation failed: ${errorToLogMessage(error)}`
      );
      return { status: "bridge_invocation_failed" };
    }
  };

export const iosGetNotificationState = async (): Promise<{
  status: "notDetermined" | "authorized" | "denied";
} | null> => {
  try {
    // Native iOS returns just the status string, not an object
    const status = await createIOSCallbackPromise(
      "getNotificationState",
      IOSNotificationStatusSchema
    );
    return { status };
  } catch (error) {
    console.error(
      `iosGetNotificationState failed: ${errorToLogMessage(error)}`
    );
    return null;
  }
};

export const iosDeleteCookies = (): void => {
  const request = {
    method: "deleteCookies",
  };
  if (isIOSApp()) getServicesHandler()?.postMessage(request);
};

export const iosOpenNewTab = (url: string): void => {
  const request = {
    method: "open",
    url,
  };
  if (isIOSApp()) getServicesHandler()?.postMessage(request);
};

const IOSAppleSignInResultSchema = z.union([
  IOSSignInErrorSchema,
  IOSAppleSignInSuccessSchema,
]);
const IOSGoogleSignInResultSchema = z.union([
  IOSSignInErrorSchema,
  IOSGoogleSignInSuccessSchema,
]);

// --- Persistent auth callback (survives iOS webview reloads) ---

export type IOSAppleSignInResult = z.infer<typeof IOSAppleSignInResultSchema>;
export type IOSGoogleSignInResult = z.infer<typeof IOSGoogleSignInResultSchema>;
export type IOSAuthRecoveryData =
  | { provider: "apple"; result: IOSAppleSignInResult }
  | { provider: "google"; result: IOSGoogleSignInResult };

// Matches the `${string}Callback_${string}` pattern in global.d.ts
const IOS_AUTH_CALLBACK = "startSignInFlowCallback_persistent" as const;
const PendingIOSAuthSchema = z.object({
  provider: z.enum(["apple", "google"]),
});
type PendingIOSAuth = z.infer<typeof PendingIOSAuthSchema>;

const pendingIOSAuthStore = sessionStore<PendingIOSAuth | null>(
  "pending_ios_auth",
  null,
  PendingIOSAuthSchema.nullable()
);

let authResolve: ((data: unknown) => void) | null = null;
let authReject: ((error: Error) => void) | null = null;
let authTimeoutId: number | null = null;

// Recovery handler — set by firebase.ts to avoid circular dep (firebase→ios is fine, ios→firebase is not)
let onAuthRecovery: ((data: IOSAuthRecoveryData) => void) | null = null;

export function setIOSAuthRecoveryHandler(
  handler: (data: IOSAuthRecoveryData) => void
): void {
  onAuthRecovery = handler;
}

/**
 * Registers a permanent callback on `window` under a fixed name.
 * If there is an active auth promise, the callback resolves it (normal flow).
 * If the page reloaded mid-auth (promise gone), it dispatches to the recovery handler.
 */
function initPersistentAuthCallback(): void {
  window[IOS_AUTH_CALLBACK] = (...args: unknown[]): void => {
    const data = args.length === 1 ? args.at(0) : args;

    if (authResolve !== null) {
      // Normal flow: resolve the active promise
      authResolve(data);
      return;
    }

    // Recovery flow: page reloaded during auth, promise is gone
    const pending = pendingIOSAuthStore.get();
    if (pending === null) return;
    pendingIOSAuthStore.set(null);
    const { provider } = pending;

    const schema =
      provider === "apple"
        ? IOSAppleSignInResultSchema
        : IOSGoogleSignInResultSchema;
    const resultParsed = schema.safeParse(data);
    if (!resultParsed.success || onAuthRecovery === null) return;

    onAuthRecovery({
      provider,
      result: resultParsed.data,
    } as IOSAuthRecoveryData);
  };
}

// Register on every page load (including after iOS-triggered reloads)
initPersistentAuthCallback();

// --- FCM token refresh callback (pushed from native when Firebase SDK refreshes the token) ---

let onTokenRefresh: ((token: string) => void) | null = null;

export function setIOSTokenRefreshHandler(
  handler: (token: string) => void
): void {
  onTokenRefresh = handler;
}

function initTokenRefreshCallback(): void {
  if (getCurrentOS() !== "ios") return;
  window.__onNativeFCMTokenRefresh = (token: string): void => {
    if (onTokenRefresh !== null) onTokenRefresh(token);
  };
}

// Register on every page load
initTokenRefreshCallback();

// --- Foreground push callback (pushed from native when an FCM push arrives while the app is open) ---

let onForegroundPush: ((payload: unknown) => void) | null = null;

export function setIOSForegroundPushHandler(
  handler: (payload: unknown) => void
): void {
  onForegroundPush = handler;
}

function initForegroundPushCallback(): void {
  if (getCurrentOS() !== "ios") return;
  window.__onNativePushReceived = (payload: unknown): void => {
    if (onForegroundPush !== null) onForegroundPush(payload);
  };
}

// Register on every page load
initForegroundPushCallback();

// --- Status-bar tap → scroll-to-top callback (iOS delivers the tap natively) ---
// The web content scrolls inside CSS overflow containers, not the WKWebView's own
// UIScrollView, so the native status-bar scroll-to-top never reaches it. Native
// forwards the tap here; we scroll every vertically-scrolled container back to the
// top, mirroring Safari's behaviour.

function initScrollToTopCallback(): void {
  if (getCurrentOS() !== "ios") return;
  window.__onNativeScrollToTop = (): void => {
    if (window.scrollY > 0) window.scrollTo({ top: 0, behavior: "smooth" });
    for (const element of document.querySelectorAll<HTMLElement>("*")) {
      if (element.scrollTop <= 0) continue;
      const overflowY = getComputedStyle(element).overflowY;
      const scrollable = overflowY === "auto" || overflowY === "scroll";
      if (scrollable && element.scrollHeight > element.clientHeight) {
        element.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  };
}

// Register on every page load
initScrollToTopCallback();

function cleanupAuthPromise(): void {
  if (authTimeoutId !== null) {
    window.clearTimeout(authTimeoutId);
    authTimeoutId = null;
  }
  authResolve = null;
  authReject = null;
}

/**
 * Auth-specific bridge that uses a fixed callback name instead of random ones.
 * The fixed name survives page reloads because `initPersistentAuthCallback()`
 * re-registers it at module load.
 */
function iosAuthBridge<T extends z.ZodTypeAny>(
  provider: "apple" | "google",
  schema: T
): Promise<z.infer<T>> {
  const handler = getServicesHandler();
  if (handler === null) {
    return Promise.reject(new Error("Not running on native iOS"));
  }

  // Cancel any in-flight auth promise
  if (authReject !== null) {
    authReject(new Error("Auth superseded by new request"));
  }
  cleanupAuthPromise();

  pendingIOSAuthStore.set({ provider });

  return new Promise<z.infer<T>>((resolve, reject) => {
    authTimeoutId = window.setTimeout(() => {
      cleanupAuthPromise();
      pendingIOSAuthStore.set(null);
      reject(new Error("iOS callback timeout: startSignInFlow"));
    }, OAUTH_TIMEOUT_MS);

    authResolve = (data: unknown) => {
      cleanupAuthPromise();
      pendingIOSAuthStore.set(null);
      const parsed = schema.safeParse(data);
      if (parsed.success) {
        resolve(parsed.data);
      } else {
        const issuesSummary = summarizeZodIssues(parsed.error.issues);
        console.error(
          `iOS callback validation failed method=startSignInFlow payload=${safeStringify(
            data
          )} issues=${issuesSummary}`
        );
        reject(new Error("iOS callback validation failed: startSignInFlow"));
      }
    };

    authReject = (error: Error) => {
      cleanupAuthPromise();
      pendingIOSAuthStore.set(null);
      reject(error);
    };

    try {
      handler.postMessage({
        method: "startSignInFlow",
        callback: IOS_AUTH_CALLBACK,
        authProvider: provider,
      });
    } catch (error) {
      cleanupAuthPromise();
      pendingIOSAuthStore.set(null);
      console.error(
        `iOS callback invocation failed method=startSignInFlow error="${errorToLogMessage(
          error
        )}"`
      );
      reject(error instanceof Error ? error : new Error(String(error)));
    }
  });
}

export const iosAppleSignIn = (): Promise<IOSAppleSignInResult> =>
  iosAuthBridge("apple", IOSAppleSignInResultSchema);

export const iosGoogleSignIn = (): Promise<IOSGoogleSignInResult> =>
  iosAuthBridge("google", IOSGoogleSignInResultSchema);

export const iosAuthSignOut = (): void => {
  if (!isIOSApp()) return;
  getServicesHandler()?.postMessage({ method: "signOut" });
};

export const iosGetSafeArea = async (): Promise<{
  top: number;
  bottom: number;
}> => {
  const [top, bottom] = await createIOSCallbackPromise(
    "getSafeArea",
    IOSSafeAreaSchema
  );
  return { top, bottom };
};

export const iosDismissKeyboard = async (): Promise<void> => {
  const request = {
    method: "dismissKeyboard",
  };
  if (isIOSApp()) getServicesHandler()?.postMessage(request);
};

export const iosRequestReview = async (): Promise<void> => {
  const request = {
    method: "requestReview",
  };

  if (isIOSApp()) getServicesHandler()?.postMessage(request);
};

export const iosSetAppUrl = async (url: string): Promise<void> => {
  const request = {
    method: "setAppUrl",
    url,
  };
  if (isIOSApp()) getServicesHandler()?.postMessage(request);
};

export const iosGetAppUrl = async (): Promise<string | null> => {
  const url = await createIOSCallbackPromise("getAppUrl", z.string());
  return url || null;
};

export const iosResetAppUrl = async (): Promise<void> => {
  const request = {
    method: "resetAppUrl",
  };
  getServicesHandler()?.postMessage(request);
};

export const iosGenerateHaptic = (name = "selection"): void => {
  const request = {
    method: "generateHapticFeedback",
    name, // selection, success, error, blender
  };
  if (isIOSApp() && !disableHapticStore.get())
    getServicesHandler()?.postMessage(request);
};

export const iosStopHaptic = (): void => {
  const request = {
    method: "stopHapticFeedback",
  };
  if (isIOSApp()) getServicesHandler()?.postMessage(request);
};

export const iosPlaySound = (name = SoundEffect.LIKE): void => {
  const request = {
    method: "playSoundEffect",
    name,
  };
  if (isIOSApp() && !disableSoundStore.get())
    getServicesHandler()?.postMessage(request);
};

export const iosOffline = (): void => {
  const request = {
    method: "offline",
  };
  if (isIOSApp()) getServicesHandler()?.postMessage(request);
};

export const iosSetClipboard = (text: string): void => {
  if (isIOSApp())
    getServicesHandler()?.postMessage({ method: "setClipboard", text });
};

export const iosRequestClipboardAccess = async (): Promise<string | null> => {
  if (!isIOSApp()) {
    return null;
  }
  return createIOSCallbackPromise(
    "requestClipboardAccess",
    z.string().nullable(),
    {},
    undefined,
    null
  );
};

export function iosOpenPushSettings(): void {
  if (isIOSApp())
    getServicesHandler()?.postMessage({ method: "openPushSettings" });
}

export const iosSaveImage = async (
  base64Image: string,
  filename: string
): Promise<boolean> => {
  return createIOSCallbackPromise("saveImage", z.boolean(), {
    image: base64Image,
    filename,
  });
};
